import { IResource } from "@gettactic/api";
import { useCloseAnimationTimeout } from "@gettactic/helpers/src/hooks/useCloseAnimationTimeout";
import { useGlobalEscapeKey } from "@gettactic/helpers/src/hooks/useGlobalEscapeKey";
import { Card } from "@v2/components/display/Card/Card";
import { MobileMapBottomMargin } from "@v2/components/display/Card/classes";
import { Image } from "@v2/components/display/Image/Image";
import { UserLabel } from "@v2/components/labels/UserLabel/UserLabel";
import { Button } from "@v2/components/ui/button";
import { ScrollArea } from "@v2/components/ui/scroll-area";
import { useIsMobile } from "@v2/hooks/use-mobile";
import { ArrowDownIcon, FavoriteIcon } from "@v2/icons";
import { cn } from "@v2/lib/utils";
import { MapSGroupedResources } from "@webV2/components/cards/MapFiltersCard/MapGroupedResources";
import { useMap } from "@webV2/map/MapProvider";
import React from "react";
import { createPortal } from "react-dom";

const classes = {
  Card: `absolute inset-0 top-32 md:top-0 z-sticky ${MobileMapBottomMargin} md:m-4 p-0 overflow-hidden data-[state=open]:zoom-in-95 data-[state=open]:slide-in-from-bottom-2/3 data-[state=open]:fade-in-0 data-[state=open]:animate-in data-[state=closed]:zoom-out-90 data-[state=closed]:fade-out-0 data-[state=closed]:animate-out`,
  CardContent: "p-0 overflow-hidden h-full flex flex-col",
  CoverWrapper: "relative h-60 w-full overflow-hidden",
  CoverImage: "object-fit opacity-10 blur-xl",
  CoverControlsWrapper: "absolute inset-0 p-5",
  CoverControlsContainer: "flex justify-between",
  BackButton: "rounded-full bg-black",
  BackIcon: "size-4 text-white",
  FavoriteButton: "bg-white",
  FavoriteIcon: "size-4 text-neutral-600",
  ScrollArea: "flex flex-1 relative",
  ScrollAreaContent: "flex flex-col gap-2 p-3 absolute inset-0",
  ActionButtonsWrapper: "p-3 flex flex-col gap-2.5"
};

export const MapUserCard: React.FC = () => {
  const { events, startDate, floor, schedulable, userFocusDisclosure } =
    useMap();

  const isMobile = useIsMobile();

  const isOpen = userFocusDisclosure.isOpen && !!userFocusDisclosure.data;
  const onClose = () => {
    userFocusDisclosure.close();
  };

  useGlobalEscapeKey(onClose);

  const { isVisible } = useCloseAnimationTimeout(isOpen);
  const user = userFocusDisclosure.data;

  const resources = React.useMemo(() => {
    if (!user?.id) return [];
    return schedulable?.filter((resource) => {
      return (
        resource.schedules?.some((schedule) =>
          schedule.slots?.some((slot) => {
            return slot.user?.id === user.id;
          })
        ) || false
      );
    });
  }, [user?.id, schedulable]);

  if (!isVisible || !user?.id) return null;

  const onResourceClick = (resource: IResource | null) => {
    //events.trigger('onHighlightUserResource', resource);
  };

  const setResourceIdHovering = (resource: IResource | null) => {
    events.trigger("onHighlightResource", resource, user?.id);
  };

  const coverImageUrl = user.profile_photo_url;

  const content = (
    <Card
      className={classes.Card}
      contentProps={{ className: classes.CardContent }}
      hasShadow={false}
      data-state={isOpen ? "open" : "closed"}
    >
      <div className={cn(classes.CoverWrapper, "bg-neutral-400")}>
        {coverImageUrl && (
          <Image src={coverImageUrl || ""} className={classes.CoverImage} />
        )}
        <div className={classes.CoverControlsWrapper}>
          <div className={classes.CoverControlsContainer}>
            <Button
              variant={"circleIcon"}
              className={classes.BackButton}
              onClick={onClose}
              size={"icon"}
            >
              <ArrowDownIcon className={classes.BackIcon} />
            </Button>
            <Button
              variant={"circleIcon"}
              className={classes.FavoriteButton}
              size={"icon"}
            >
              <FavoriteIcon className={classes.FavoriteIcon} />
            </Button>
          </div>
          <div className={"flex h-full flex-1 items-center"}>
            <div className={"flex w-full items-center gap-2"}>
              {user && <UserLabel showEmail className={"flex-1"} user={user} />}
            </div>
          </div>
        </div>
      </div>
      <ScrollArea className={classes.ScrollArea}>
        <div className={classes.ScrollAreaContent}>
          <div className={"relative h-full flex-1"}>
            {/*{
              getResourcesVirtualizedListTab({
                value: 'rooms',
                label: 'Rooms',
                resources: resources || [],
                setResourceIdHovering,
                onReserve: onResourceClick,
                showSchedules: true,
                showScheduleOfUserId: user?.id
              })?.content
            }*/}

            <MapSGroupedResources
              users={[]}
              resources={resources || []}
              setResourceIdHovering={setResourceIdHovering}
              onBook={onResourceClick}
              showScheduleOfUserId={user?.id}
              showAllSchedulesAsResources
              showSchedules
            />
          </div>
        </div>
      </ScrollArea>
    </Card>
  );

  if (isMobile) return createPortal(content, window.document.body);

  return content;
};
